import React, { useState, useEffect, useCallback, useContext } from 'react';
import styles from './styles.module.css';
import AlertHelper from 'helpers/AlertHelper';
import { useParams } from 'react-router-dom';
import Error from 'components/utils/Alert/Error';
import Success from 'components/utils/Alert/Success';
import SearchIcon from '@mui/icons-material/Search';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { Grid, Button, Tooltip,IconButton, } from '@mui/material';
import 'date-fns';
import SyncIcon from '@mui/icons-material/Sync';
import { Paper, TablePagination, Typography, Skeleton } from '@mui/material';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AddPaymentToVendorPopup from 'components/popups/AddPaymentToVendorPopup';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { saveAs } from 'file-saver';
import { ShopContext } from 'Context/ShopContext';
import VendorServices from 'services/VendorServices';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import PurchaseService from 'services/PurchaseService';
import VendorPurchaseDialog from 'components/popups/VendorPurchaseDialog';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';


function VendorDetails() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);

  const [loadingIndicator, setLoadingIndicator] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [vendorJournals, setVendorJournals] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);
  const [count, setCount] = useState(0);
  const { startDate, endDate } = dynamicDateRange('VENDOR_DETAILS');
  const [fromDate, setFromDate] = useState(startDate);
  const [toDate, setToDate] = useState(endDate);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(false);
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate,
    toDate,
  });
  const [paymentTab, setPaymentTab] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const { vendorId } = useParams();

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    purchase_id: tCommon('Purchase ID'),
    date: {
      title: tCommon('Date'),
      getValue: (item) => {
        return DateTimeHelpers.convertDateToIsoDMY(item.time);
      },
    },
    time: {
      title: tCommon('Time'),
      getValue: (item) => {
        const date = item.time.split('T')[1].split('.')[0];
        return DateTimeHelpers.formatServerTimeSegment(date);
      },
    },
    description: tCommon('Description'),
    debit: {
      title: tCommon('Debit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.debit || 0);
      },
    },
    credit: {
      title: tCommon('Credit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.credit || 0);
      },
    },
    balance: {
      title: tCommon('Balance'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.balance || 0);
      },
    },
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(vendorJournals, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `TRANSACTIONS-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to csv successfully'),
      type: 'success',
    });
  };

  const getBalance = (data) => {
    const balance = ShopsHelper.getAmountFormatted(shop, data?.balance);
    let balanceStyle;
    if (balance < 0) balanceStyle = { color: 'red' };
    else balanceStyle = { color: 'green' };
    return (
      <Typography variant="body2" style={balanceStyle} gutterBottom>
        {formatBalanceWithCr(balance)}
      </Typography>
    );
  };

  const headerData = [
    {
      label: tCommon('Date'),
      id: 'Date',
      type: 'callback',
      viewRender: (item) => {
        if (!item || !item.time) {
          return '';
        }
        return `${DateTimeHelpers.convertDateToIsoDMY(item.time)} ${DateTimeHelpers.convertTo12HourTime(item.time)}`;
      },
    },
    {
      label: tCommon('Account Head'),
      id: 'payment_mode',
      type: 'callback',
      viewRender: (item) => item?.payment_mode?.name || item?.mode?.name || item?.payment_mode_name || item?.payment_mode || item?.mode || '-',
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Credit'),
      id: 'credit',
      type: 'callback',
      viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.credit || 0),
    },
    {
      label: tCommon('Debit'),
      id: 'debit',
      type: 'callback',
      viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.debit || 0),
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'callback',
      viewRender: (data) => getBalance(data),
    },
  ];

  const getVendorJournals = async ({
    _fromDate = fromDate,
    _toDate = toDate,
    _offset = offset,
    _limit = limit,
  } = {}) => {
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = new Date(_fromDate.getTime() - _fromDate.getTimezoneOffset() * 60000);
      const timezoneFreeEndTime = new Date(_toDate.getTime() - _toDate.getTimezoneOffset() * 60000);

      const params = {
        start_time: timezoneFreeStartTime,
        end_time: timezoneFreeEndTime,
        offset: _offset,
        limit: _limit,
        search: filterText,
      };

      const res = await VendorServices.getVendorJournals(vendorId, params);
      let journals = [...res?.data];

      journals.sort((a, b) => {
        const timeA = new Date(a.payment_time || a.time || a.created_at || 0).getTime();
        const timeB = new Date(b.payment_time || b.time || b.created_at || 0).getTime();
        if (timeA !== timeB) return timeA - timeB;
        return a.id - b.id;
      });

      setVendorJournals(journals);
      setCount(res?.count);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };

  const getVendorDetails = async () => {
    try {
      const res = await VendorServices.getVendor(vendorId);
      setVendor(res);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
  };

  const addPaymentToVendor = async (data) => {
    setLoadingIndicator(true);
    try {
      await VendorServices.addPaymentToVendor(vendorId, data);
      togglePaymentTab();
      setPage(0);
      setOffset(0);
      getVendorDetails();
      getVendorJournals({ _offset: 0 });
      AlertHelper.setShortMessage(setSuccessMsg, tCommon('Payment added successfully'));
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };

  const togglePaymentTab = () => setPaymentTab((prevValue) => !prevValue);

  const togglePurchaseView = async (row) => {
    if (row === null || row?.purchase_id === null) return setSelectedPurchase(null);
    setLoadingIndicator(true);
    try {
      const res = await PurchaseService.getPurchase(row?.purchase_id);
      (res?.items || []).forEach((item) => (item.total = item.quantity * item.rate + item.vat));
      setSelectedPurchase(res);
    } catch (error) {
      AlertHelper.setShortMessage(setErrorMsg, error.message);
    }
    setLoadingIndicator(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const _offset = newPage * limit;
    setOffset(_offset);
    getVendorJournals({ _offset });
  };

  const handleChangeRowsPerPage = (event) => {
    const _limit = +event.target.value;
    setLimit(_limit);
    getVendorJournals({ _limit });
  };

  const generateVendorJournals = () => {
    getVendorJournals();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate,
      toDate,
    });
  };

  const handleSearch = () => {
    setPage(0);
    setOffset(0);
    getVendorJournals({ _offset: 0 });
  };

  const isAnyChangeOnvendorDetailsFilters = useCallback(() => {
    return fromDate !== prevFilterData.fromDate || toDate !== prevFilterData.toDate;
  }, [fromDate, toDate, prevFilterData.fromDate, prevFilterData.toDate]);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnvendorDetailsFilters());
  }, [isAnyChangeOnvendorDetailsFilters, isShowGenerateButton]);

  useEffect(() => {
    getVendorDetails();
    getVendorJournals();
    //eslint-disable-next-line
  }, [vendorId]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Transaction')}
          <span className={styles.menuTitle}>{tCommon('Details & History')}</span>
        </h2>
        {shop && shop?.vendor_support_validity !== null && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
          </div>
        )}
      </div>
      {shop && shop.customer_support_validity == null && (
        <RestrictionInfo
          title={tCommon('Vendor Support Feature not available')}
          content={tCommon('To get Vendor support options, contact support team.')}
        />
      )}

      {shop && shop.vendor_support_validity !== null && (
        <>
          <div className={styles.changeable}>
            <Grid container spacing={2}>
              <Grid item xs={12} className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tManagement('Vendor Transactions Details for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(startDate),
                    to: DateTimeHelpers.convertDateToDMY(endDate),
                  })}
                </h2>
              </Grid>
              <Grid item xs={12} className={styles.actionButtons}>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={6} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Vendor Payment')}</label>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="small"
                      style={{ backgroundColor: '#ff851b', height: 40 }}
                      className={styles.actionBtn}
                      onClick={togglePaymentTab}
                    >
                      <RequestQuoteOutlinedIcon className={styles.actionBtnIcon} />
                      {tCommon('Add Payment')}
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Search Transactions')}</label>
                    </div>
                    <div className={styles.searchSec}>
                      <input
                        type="text"
                        value={filterText}
                        onChange={(e) => {
                          setFilterText(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                        className={styles.searchInput}
                        placeholder={tCommon('search transactions')}
                      />
                      <Tooltip title={tCommon('Search')} placement="bottom">
                        <IconButton onClick={handleSearch}>
                          <SearchIcon className={styles.searchIcon} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <DateTimeRange
                      storageKey="VENDOR_DETAILS"
                      setFilterFromDate={setFromDate}
                      setFilterToDate={setToDate}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <div style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Generate Report')}</label>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      className={styles.actionBtn}
                      style={{ backgroundColor: '#00a65a', height: 40 }}
                      onClick={generateVendorJournals}
                      disabled={isShowGenerateButton === false}
                    >
                      <SyncIcon className={styles.actionBtnIcon} />
                      {tCommon('Generate')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
          <div className={styles.changeable} style={{ paddingBottom: '15px' }}>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Name')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator && !vendor ? <Skeleton variant="text" width="60%" animation="wave" /> : vendor?.name || '-'}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Code')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator && !vendor ? <Skeleton variant="text" width="40%" animation="wave" /> : vendor?.code || 0}
                </h3>
              </Grid>
            </Grid>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Mobile')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator && !vendor ? <Skeleton variant="text" width="50%" animation="wave" /> : vendor?.mobile || '-'}
                </h3>
              </Grid>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Balance')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator && !vendor ? <Skeleton variant="text" width="30%" animation="wave" /> : formatBalanceWithCr(vendor?.balance || 0)}
                </h3>
              </Grid>
            </Grid>
            <Grid container alignItems="center" style={{ paddingLeft: '10px', paddingTop: '5px' }} spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Address')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>
                  {loadingIndicator && !vendor ? <Skeleton variant="text" width="80%" animation="wave" /> : vendor?.address || '-'}
                </h3>
              </Grid>
            </Grid>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {successMsg && (
            <div className={styles.marginTop}>
              <Success title={successMsg} />
            </div>
          )}

          {/* TRANSACTOINS SECTION */}
          <Grid className={styles.changeable}>
            <div className={styles.headTitle}>
              <h2 className={styles.subTitle}>{tCommon('Transaction History')}</h2>
            </div>
            {(vendorJournals || loadingIndicator) &&
              ((vendorJournals && vendorJournals.length) || loadingIndicator ? (
                <>
                  <Paper className={styles.tableWrapper}>
                    <div className={styles.tableContainer}>
                      <Grayout open={isShowGenerateButton} />
                      <DataTable
                        columns={headerData}
                        rows={vendorJournals || []}
                        isLoading={loadingIndicator}
                        rowClickListener={togglePurchaseView}
                      />
                    </div>
                    <TablePagination
                      rowsPerPageOptions={[20, 50]}
                      component="div"
                      count={count}
                      rowsPerPage={limit}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </Paper>
                </>
              ) : (
                !loadingIndicator && (
                  <Info
                    title={tCommon('No transactions to list')}
                    content={tCommon(
                      'You have no transactions to list with current filter configuration. Please clear the filters or make some purchase'
                    )}
                  />
                )
              ))}
          </Grid>
        </>
      )}
      {selectedPurchase && <VendorPurchaseDialog togglePurchaseTab={togglePurchaseView} rowData={selectedPurchase} />}
      {paymentTab && (
        <AddPaymentToVendorPopup
          togglePaymentTab={togglePaymentTab}
          addPaymentToVendor={addPaymentToVendor}
          currentBalance={vendor?.balance || 0}
        />
      )}
    </div>
  );
}

export default withConsoleBase(VendorDetails);
